import { Code, Palette, Zap, Users } from "lucide-react";
import { AnimateIn } from "@/hooks/useAnimations";

const highlights = [
  { icon: Code, label: "Clean Code", desc: "Modern tech stack for fast, responsive sites" },
  { icon: Palette, label: "Unique Design", desc: "No templates. Every site is custom-crafted" },
  { icon: Zap, label: "Fast Delivery", desc: "Your website live in as little as 7 days" },
  { icon: Users, label: "India Focused", desc: "Built for Indian businesses across the country" },
];

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-14 sm:py-20 lg:py-24 bg-[#0A0A0A] relative dot-pattern scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header - centered */}
        <AnimateIn>
          <div className="text-center mb-10 sm:mb-16">
            <p className="section-label text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-red-600 justify-center">About KoKo Sites</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">
              Crafting Digital Experiences <span className="text-red-600">For India</span>
            </h2>
            <div className="section-divider"></div>
          </div>
        </AnimateIn>

        {/* Content grid - on mobile/tablet, cards show first then intro; desktop keeps side-by-side order */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <AnimateIn direction="left" className="order-2 lg:order-1">
            <div className="space-y-5 text-zinc-400 leading-relaxed">
              <p className="text-base">
                Hi, I'm <strong className="text-white">Kamran Zahin Aziz</strong>, the founder and lead designer at KoKo Sites.
                I'm passionate about building beautiful, high-performance websites — and I started KoKo Sites
                to help small businesses and professionals across India establish a powerful online presence.
              </p>
              <p>
                From gym owners in Mumbai to lawyers in Chennai, I understand what Indian businesses need.
                Every website I build is designed to attract customers, build trust, and drive growth.
              </p>
              <p>
                I believe great design shouldn't cost a fortune. That's why KoKo Sites offers premium web
                design at honest, affordable prices that work for Indian businesses.
              </p>
              <div className="pt-4">
                <a href="#contact" data-testid="about-cta" className="btn-brutal btn-shine inline-flex items-center gap-2 bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 text-xs border-2 border-white hover:bg-white hover:text-black">
                  Let's Talk &rarr;
                </a>
              </div>
            </div>
          </AnimateIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 order-1 lg:order-2">
            {highlights.map((item, i) => (
              <AnimateIn key={item.label} direction="right" delay={0.18 * i}>
                <div
                  className="card-accent card-top-bar border border-white/10 bg-[#111111] p-4 sm:p-6 hover:border-red-600/60 transition-all duration-300 hover:-translate-y-2 hover:shadow-[6px_6px_0_0_rgba(229,9,20,0.8)] group cursor-pointer h-full"
                  data-testid={`about-highlight-${i}`}
                >
                  <item.icon className="text-red-600 mb-3 sm:mb-4 group-hover:scale-125 group-hover:rotate-12 transition-transform duration-300" size={24} />
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-2 group-hover:text-red-500 transition-colors">{item.label}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
